import ItemCard from '../components/item-card.gts';
import PlaceMap from '../components/place-map.gts';
import MobileHeader from '../components/mobile-header.gts';
import type { TOC } from '@ember/component/template-only';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import type PlacesController from '../controllers/places.ts';
import pullToRefresh from '../modifiers/pull-to-refresh.ts';
import tw from '../helpers/tw';

interface Signature {
  Args: {
    model: object;
    controller: PlacesController;
  };
}

const PlacesTemplate: TOC<Signature> = <template>
  {{#if @controller.isMapTab}}
    {{! Map view: Full screen with toggle in header }}
    <MobileHeader @noSpacer={{true}}>
      <div
        class="tabs tabs-boxed border-base-300 bg-base-100/50 scrollbar-hide flex-nowrap overflow-x-auto"
      >
        <button
          type="button"
          class={{tw "tab" (if @controller.isMapTab "tab-active")}}
          {{on "click" (fn @controller.setTab "map")}}
        >
          🗺 Map
        </button>
        <button
          type="button"
          class={{tw "tab" (if @controller.isListTab "tab-active")}}
          {{on "click" (fn @controller.setTab "list")}}
        >
          📋 List
        </button>
      </div>
    </MobileHeader>

    {{#if @controller.markers.length}}
      {{! Mobile: Full screen map from header to dock }}
      <div
        class="fixed top-[calc(3.5rem+env(safe-area-inset-top))] right-0 bottom-[calc(4rem+env(safe-area-inset-bottom))] left-0 z-0 lg:hidden"
      >
        <PlaceMap
          @markers={{@controller.markers}}
          @fullscreen={{true}}
          @userLocation={{@controller.userLocation}}
          @alwaysInteractive={{true}}
        />
      </div>
      {{! Desktop: Normal map container }}
      <div class="hidden lg:block">
        <PlaceMap
          @markers={{@controller.markers}}
          @fullscreen={{true}}
          @userLocation={{@controller.userLocation}}
          @alwaysInteractive={{true}}
        />
      </div>
    {{else if @controller.loadError}}
      <div
        class="bg-base-100 fixed top-[calc(3.5rem+env(safe-area-inset-top))] right-0 bottom-[calc(4rem+env(safe-area-inset-bottom))] left-0 z-0 flex items-center justify-center lg:hidden"
      >
        <div class="text-center">
          <p class="text-error mb-3 text-sm">Failed to load places.</p>
          <button
            type="button"
            class="btn btn-sm btn-outline"
            {{on "click" @controller.loadData}}
          >Retry</button>
        </div>
      </div>
      <div class="hidden lg:block">
        <div class="py-8 text-center">
          <p class="text-error mb-3 text-sm">Failed to load places.</p>
          <button
            type="button"
            class="btn btn-sm btn-outline"
            {{on "click" @controller.loadData}}
          >Retry</button>
        </div>
      </div>
    {{else}}
      <div
        class="bg-base-100 fixed top-[calc(3.5rem+env(safe-area-inset-top))] right-0 bottom-[calc(4rem+env(safe-area-inset-bottom))] left-0 z-0 flex items-center justify-center lg:hidden"
      >
        <p class="text-base-content/60 text-center">
          No places with map coordinates found.
        </p>
      </div>
      <div class="hidden lg:block">
        <div class="text-base-content/60 py-8 text-center">
          No places with map coordinates found.
        </div>
      </div>
    {{/if}}
  {{else}}
    {{! List view: Normal layout with header and toggle below }}
    <div>
      <MobileHeader @title="Places" />

      <h1 class="mb-2 hidden text-3xl font-bold lg:block">Places</h1>
      <p class="text-base-content/70 mb-4 text-sm">
        {{@controller.items.length}}
        places in catalog ·
        {{@controller.mappedCount}}
        with map coordinates
      </p>

      <div
        class="tabs tabs-box scrollbar-hide mb-4 flex-nowrap overflow-x-auto"
      >
        <button
          type="button"
          class={{tw "tab" (if @controller.isMapTab "tab-active")}}
          {{on "click" (fn @controller.setTab "map")}}
        >
          🗺 Map
        </button>
        <button
          type="button"
          class={{tw "tab" (if @controller.isListTab "tab-active")}}
          {{on "click" (fn @controller.setTab "list")}}
        >
          📋 List
        </button>
      </div>

      <div {{pullToRefresh @controller.refresh}}>
        {{#if @controller.items.length}}
          <div class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {{#each @controller.items as |item|}}
              <ItemCard
                @item={{item}}
                @userLocation={{@controller.userLocation}}
              />
            {{/each}}
          </div>
        {{else if @controller.loadError}}
          <div class="py-8 text-center">
            <p class="text-error mb-3 text-sm">Failed to load places.</p>
            <button
              type="button"
              class="btn btn-sm btn-outline"
              {{on "click" @controller.loadData}}
            >Retry</button>
          </div>
        {{else}}
          <div class="text-base-content/60 py-8 text-center">
            No places found.
          </div>
        {{/if}}
      </div>
    </div>
  {{/if}}
</template>;

export default PlacesTemplate;
